import { Button, Group, Modal, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { useEffect, useState } from 'react';
import { ChatService } from '../../services/chatService';

interface AIAssistantModalProps {
  opened: boolean;
  onClose: () => void;
  onApply: (text: string, profileText?: string) => void;
  initialText?: string;
  initialProfile?: string;
  personId?: number;
}

export function AIAssistantModal({ opened, onClose, onApply, initialText = '', initialProfile = '', personId }: AIAssistantModalProps) {
  const [message, setMessage] = useState('');
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState('');

  useEffect(() => {
    if (opened) {
      setProfile(initialProfile);
    }
  }, [opened, initialProfile]);

  const handleSend = async () => {
    if (!personId) return;

    setIsLoading(true);
    try {
      const chatService = ChatService.getInstance();
      const result = await chatService.generateSummary(personId, profile);
      onApply(result.response, profile);
      onClose();
    } catch (error) {
      console.error('Failed to generate summary:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApply = () => {
    onApply(response, profile);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Generate Summary"
      size="lg"
    >
      <Stack>
        <Text size="sm" c="dimmed">
          Enter the focus below. The AI will search your CV for experience that matches it, then create a summary of qualifications from that experience.
          This lets you tailor your CV summary to a specific project, domain, or role—useful when presenting your profile for particular opportunities (e.g. resource staffing).
        </Text>

        <TextInput
          label="What type of experience should we focus on?"
          description="Profiling: the summary will be tailored to this focus so it highlights the most relevant experience for a given opportunity."
          placeholder="e.g. Team Leader, Banking-related projects, .NET developer"
          value={profile}
          onChange={(e) => setProfile(e.target.value)}
          required
        />

        <Button
          onClick={handleSend}
          loading={isLoading}
          disabled={!personId || !profile.trim()}
        >
          Generate Summary
        </Button>

        {response && (
          <>
            <Text size="sm" fw={500}>Generated Summary:</Text>
            <Textarea
              value={response}
              readOnly
              minRows={5}
              style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}
            />
            <Group justify="flex-end">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleApply}>
                Apply Changes
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
} 
